import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { PaymentState, paymentsApi } from "@/api";
import type {
  CheckoutData,
  ConfirmManualPaymentData,
  PaymentFilter,
  PaymentStateValue,
} from "@/api";
import { QUERY_KEYS } from "@/constants";

/**
 * To'lovni boshlaydi va brauzerni provayderga topshiradi.
 *
 * Yo'naltirish chaqiruvchi komponentda emas, shu yerda bo'ladi: shunda har bir
 * kirish nuqtasi — kurs kartochkasi, batafsil sahifa, reyestrdagi "to'lovni
 * qayta urinish" — bir xil ishlaydi va yiqilgan yo'naltirish jimgina yo'qolib
 * ketmasdan, bitta xabar bo'lib chiqadi.
 */
export const useCheckout = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CheckoutData) => paymentsApi.checkout(data),
    onSuccess: res => {
      const url = res.data?.paymentUrl;
      if (!url) {
        toast.error(t("PAYMENTS.NO_URL_ERROR"));
        return;
      }
      window.location.assign(url);
    },
  });
};

/** To'lovlarim tarixi. */
export const useMyPayments = (filter: PaymentFilter) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_MY_PAYMENTS, filter],
    queryFn: () => paymentsApi.myPayments(filter),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

/** Bitta kursning to'lovlari — kurs egasi yoki xodim uchun. */
export const useCoursePayments = (courseId: number | undefined, filter: PaymentFilter) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_COURSE_PAYMENTS, courseId, filter],
    queryFn: () => paymentsApi.coursePayments(courseId as number, filter),
    enabled: !!courseId,
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

/** To'liq reyestr (moderator / admin). */
export const usePaymentLedger = (filter: PaymentFilter) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_PAYMENT_LEDGER, filter],
    queryFn: () => paymentsApi.list(filter),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

/** Reyestr plitalarida ko'rsatiladigan holatlar. */
export const LEDGER_STATES: readonly PaymentStateValue[] = [
  PaymentState.PAID,
  PaymentState.CREATED,
  PaymentState.CANCELLED,
  PaymentState.CANCELLED_AFTER_PAID,
];

/**
 * Holat bo'yicha tranzaksiya sonlari.
 *
 * Backend'da "payments/stats" endpoint'i YO'Q, shuning uchun har bir holat
 * uchun `limit: 1` bilan bitta so'rov ketadi va `meta.total` o'qiladi —
 * bir qatorlik javob, lekin son HAQIQIY. Yig'ma endpoint qo'shilsa,
 * `docs/analytics-endpoints.md` dagi shakl shu hookni almashtiradi.
 */
export const usePaymentStateCounts = () => {
  const queries = useQueries({
    queries: LEDGER_STATES.map(state => ({
      queryKey: [QUERY_KEYS.GET_PAYMENT_LEDGER, "count", state],
      queryFn: () => paymentsApi.list({ page: 1, limit: 1, state }),
      select: (res: Awaited<ReturnType<typeof paymentsApi.list>>) => res.meta.total,
    })),
  });

  return {
    counts: {
      paid: queries[0]?.data,
      created: queries[1]?.data,
      cancelled: queries[2]?.data,
      refunded: queries[3]?.data,
    },
    isLoading: queries.some(query => query.isLoading),
    isError: queries.some(query => query.isError),
  };
};

const useInvalidatePayments = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PAYMENT_LEDGER] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE_PAYMENTS] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_PAYMENTS] });
    // Tasdiqlash yoki qaytarish yozilishni ham o'zgartiradi — ro'yxat eskiradi.
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE_ROSTER] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_ENROLLMENTS] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_ENROLLMENT_STATUS] });
  };
};

/** Naqd yoki bank orqali kelgan to'lovni qayd etadi va kursni ochadi (admin). */
export const useConfirmManualPayment = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidatePayments();
  return useMutation({
    mutationFn: (data: ConfirmManualPaymentData) => paymentsApi.confirmManual(data),
    onSuccess: () => {
      invalidate();
      toast.success(t("PAYMENTS.MANUAL_CONFIRMED_TOAST"));
    },
  });
};

/** To'langan tranzaksiyani qaytaradi va kirish huquqini bekor qiladi (admin). */
export const useRefundPayment = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidatePayments();
  return useMutation({
    mutationFn: (vars: { id: number; note?: string }) => paymentsApi.refund(vars.id, vars.note),
    onSuccess: () => {
      invalidate();
      toast.success(t("PAYMENTS.REFUNDED_TOAST"));
    },
  });
};
